import { parseJavaProperties } from './javaProperties.ts';
import { gatherAtlas, type ExportAtlas } from './atlas/mapExport.ts';
import { AUDIENCE_NAMES, ENTITY_TYPE_NAMES, type Audience } from './model.ts';
import { parseAudience, realmAudience } from './queries.ts';
import { int, text, type SqlConnection } from './sql.ts';
import { listActs, listChapters, wordCount } from './story/story.ts';
import { linkedEntryIds } from './story/sceneMentions.ts';

/**
 * The realm gathered once for the JSON, Markdown and HTML exports (docs/PARITY-STEP-1-PLAN.md
 * section 4), as the desktop's `export.RealmExportData` does: entries and collections in
 * its order, each with what every format needs. Plain data, so it crosses from the realm
 * worker to the page, which builds the files (the Markdown and HTML need an HTML parser).
 * Maps join in parity step 4.
 */

export interface ExportImage {
  readonly id: string;
  readonly fileName: string;
  /** As stored, which the desktop's JSON export writes; the HTML export checks the bytes instead. */
  readonly mimeType: string;
  readonly data: Uint8Array;
}

export interface ExportLink {
  readonly targetEntryId: string;
  /** The target's title, or its id when it no longer exists (`RealmExportData.titleOf`). */
  readonly targetTitle: string;
  readonly targetExists: boolean;
  readonly relationshipType: string;
}

export interface ExportEntry {
  readonly id: string;
  /** `LoreEntry.displayTypeName`. */
  readonly typeName: string;
  readonly title: string;
  readonly contentHtml: string;
  readonly locked: boolean;
  /** Sorted by name, as `Metadata.fromStorageText` reads them. */
  readonly metadata: readonly (readonly [string, string])[];
  /** Trimmed, lower-case and sorted, as `LoreEntry` keeps them. */
  readonly labels: readonly string[];
  /** `AudienceLevel.displayName`. */
  readonly audience: string;
  /** Its audience's role (`OWNER` to `READER`, or `CUSTOM`), for the static website. */
  readonly audienceRole: string;
  /** `RealmExportData.sortedLinks`: by type, then target title, then target id. */
  readonly links: readonly ExportLink[];
  /** Oldest first. Empty for a locked entry, as every desktop export leaves them out. */
  readonly images: readonly ExportImage[];
  /** As Java's `Instant.toString` writes them. */
  readonly created: string;
  readonly modified: string;
}

export interface ExportCollection {
  readonly id: string;
  readonly name: string;
  readonly audience: string;
  readonly audienceRole: string;
  /** By title. */
  readonly members: readonly { readonly id: string; readonly title: string }[];
}

/** One scene, with where it sits in its story (`ExportedScene`). */
export interface ExportScene {
  readonly id: string;
  readonly title: string;
  readonly actTitle: string;
  readonly chapterTitle: string;
  readonly contentHtml: string;
  readonly storyBeats: string;
  readonly words: number;
  readonly wordGoal: number;
  /** The scene's references, as the realm keeps them. */
  readonly referenceIds: readonly string[];
  /** The entries its text links to, in the order first linked. */
  readonly linkedInTextIds: readonly string[];
}

/** One Story Studio project (`ExportedStory`): its scenes in reading order, and a slug unique among the stories. */
export interface ExportStory {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly wordGoal: number;
  readonly words: number;
  readonly slug: string;
  readonly scenes: readonly ExportScene[];
}

export interface ExportData {
  readonly realm: { readonly name: string; readonly description: string; readonly audience: string };
  /** By type name, then title, both without regard to case. */
  readonly entries: readonly ExportEntry[];
  /** By name, without regard to case. */
  readonly collections: readonly ExportCollection[];
  /** By name, without regard to case. */
  readonly stories: readonly ExportStory[];
  /** The Atlas: every map, in gallery order, with the categories and icons they show. */
  readonly atlas: ExportAtlas;
}

/** Java's `String.compareTo`: UTF-16 code units, which JavaScript's `<` also compares. */
export function javaCompare(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** `util.Slugs.slugify`: lower-case ASCII letters and digits, runs of anything else as one `-`, `realm` when nothing is left. */
export function slugify(input: string): string {
  const slug = input.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-+)|(-+$)/g, '');
  return slug === '' ? 'realm' : slug;
}

/** `AudienceLevel.displayName`: the role's name, or a custom one ("Custom" when it has none). */
export function audienceDisplayName(audience: Audience): string {
  if (audience.role === 'CUSTOM') return audience.customLabel.trim() === '' ? 'Custom' : audience.customLabel;
  return AUDIENCE_NAMES[audience.role] ?? audience.role;
}

const INSTANT = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?Z$/;

/**
 * A stored time as Java's `Instant.toString` writes it (`DateTimeFormatter.ISO_INSTANT`):
 * seconds always, and a fraction only when it isn't zero, in groups of three digits. The
 * web stores `toISOString()` times, which always have milliseconds.
 */
export function javaInstant(stored: string): string {
  const match = INSTANT.exec(stored.trim());
  if (!match) return stored;
  const [, minutes, seconds = '00', fraction = ''] = match;
  const digits = fraction.replace(/0+$/, '');
  const shown = digits === '' ? '' : `.${digits.padEnd(Math.ceil(digits.length / 3) * 3, '0')}`;
  return `${minutes}:${seconds}${shown}Z`;
}

const byJava = <T>(key: (item: T) => string) => (a: T, b: T) => javaCompare(key(a), key(b));

export function gatherExport(conn: SqlConnection): ExportData {
  const realmRow = conn.all('SELECT name, description FROM realm_metadata LIMIT 1')[0] ?? {};

  // findAll's order (title, without regard to ASCII case), then sorted - stably - by type and title.
  const rows = conn.all(
    `SELECT id, entity_type, custom_type_name, title, content_html, metadata, audience, locked, created, modified
     FROM lore_entries ORDER BY title COLLATE NOCASE`,
  );
  const titles = new Map(rows.map((row) => [text(row['id']), text(row['title'])]));
  const entries: ExportEntry[] = rows.map((row) => {
    const id = text(row['id']);
    const entityType = text(row['entity_type']);
    const custom = text(row['custom_type_name']);
    const locked = int(row['locked']) !== 0;
    const labels = [
      ...new Set(
        conn
          .all('SELECT label FROM entry_labels WHERE entry_id = ?', [id])
          .map((r) => text(r['label']).trim().toLowerCase())
          .filter((label) => label !== ''),
      ),
    ].sort(javaCompare);
    const links = conn
      .all('SELECT target_entry_id, relationship_type FROM entry_links WHERE source_entry_id = ?', [id])
      .map((r): ExportLink => {
        const target = text(r['target_entry_id']);
        const title = titles.get(target);
        return { targetEntryId: target, targetTitle: title ?? target, targetExists: title !== undefined, relationshipType: text(r['relationship_type']) };
      })
      .sort(
        (a, b) =>
          javaCompare(a.relationshipType, b.relationshipType) ||
          javaCompare(a.targetTitle.toLowerCase(), b.targetTitle.toLowerCase()) ||
          javaCompare(a.targetEntryId, b.targetEntryId),
      );
    const images: ExportImage[] = locked
      ? []
      : conn.all('SELECT id, file_name, mime_type, data FROM entry_images WHERE entry_id = ? ORDER BY created, rowid', [id]).flatMap((r) => {
          const data = r['data'];
          return data instanceof Uint8Array ? [{ id: text(r['id']), fileName: text(r['file_name']), mimeType: text(r['mime_type']), data }] : [];
        });
    return {
      id,
      typeName: entityType === 'CUSTOM' && custom.trim() !== '' ? custom : (ENTITY_TYPE_NAMES[entityType] ?? entityType),
      title: text(row['title']),
      contentHtml: text(row['content_html']),
      locked,
      metadata: [...parseJavaProperties(text(row['metadata'])).entries()],
      labels,
      audience: audienceDisplayName(parseAudience(text(row['audience']))),
      audienceRole: parseAudience(text(row['audience'])).role,
      links,
      images,
      created: javaInstant(text(row['created'])),
      modified: javaInstant(text(row['modified'])),
    };
  });
  entries.sort((a, b) => javaCompare(a.typeName.toLowerCase(), b.typeName.toLowerCase()) || javaCompare(a.title.toLowerCase(), b.title.toLowerCase()));

  const collections: ExportCollection[] = conn
    .all('SELECT id, name, audience FROM entry_collections ORDER BY name COLLATE NOCASE')
    .map((row) => {
      const id = text(row['id']);
      const members = conn
        .all(
          `SELECT e.id, e.title FROM lore_entries e JOIN entry_collection_members m ON m.entry_id = e.id
           WHERE m.collection_id = ? ORDER BY e.title COLLATE NOCASE`,
          [id],
        )
        .map((r) => ({ id: text(r['id']), title: text(r['title']) }))
        .sort(byJava((m) => m.title.toLowerCase()));
      const audience = parseAudience(text(row['audience']));
      return { id, name: text(row['name']), audience: audienceDisplayName(audience), audienceRole: audience.role, members };
    })
    .sort(byJava((c) => c.name.toLowerCase()));

  return {
    realm: { name: text(realmRow['name']), description: text(realmRow['description']), audience: audienceDisplayName(realmAudience(conn)) },
    entries,
    collections,
    stories: gatherStories(conn),
    atlas: gatherAtlas(conn),
  };
}

/** `RealmExportData.gatherStories`: every project by name, its scenes act by act, chapter by chapter. */
function gatherStories(conn: SqlConnection): ExportStory[] {
  const slugs = new Set<string>();
  return conn
    .all('SELECT id, name, description, writing_goal_words FROM story_projects ORDER BY name COLLATE NOCASE')
    .sort(byJava((row) => text(row['name']).toLowerCase()))
    .map((row) => {
      const id = text(row['id']);
      const scenes: ExportScene[] = [];
      for (const act of listActs(conn, id)) {
        for (const chapter of listChapters(conn, act.id)) {
          for (const scene of conn.all('SELECT id, title, content_html, story_beats, word_goal FROM story_scenes WHERE chapter_id = ? ORDER BY position', [chapter.id])) {
            const sceneId = text(scene['id']);
            const html = text(scene['content_html']);
            scenes.push({
              id: sceneId,
              title: text(scene['title']),
              actTitle: act.title,
              chapterTitle: chapter.title,
              contentHtml: html,
              storyBeats: text(scene['story_beats']),
              words: wordCount(html),
              wordGoal: Math.max(0, int(scene['word_goal'])),
              referenceIds: conn.all('SELECT entry_id FROM story_scene_entries WHERE scene_id = ?', [sceneId]).map((r) => text(r['entry_id'])),
              linkedInTextIds: linkedEntryIds(html),
            });
          }
        }
      }
      // `uniqueSlug(name, slugs, "story")`: the name's slug, then -2, -3... for the ones after.
      const base = slugify(text(row['name']));
      let slug = base;
      for (let n = 2; slugs.has(slug); n++) slug = `${base}-${n}`;
      slugs.add(slug);
      return {
        id,
        name: text(row['name']),
        description: text(row['description']),
        wordGoal: Math.max(0, int(row['writing_goal_words'])),
        words: scenes.reduce((sum, s) => sum + s.words, 0),
        slug,
        scenes,
      };
    });
}
